/**
 * Chart — каркас графика на Recharts.
 *
 * Компонент задаёт всё, что должно быть одинаковым у любого графика продукта:
 * цвета серий, тон сетки и осей, типографику подписей, вид тултипа и легенды,
 * поведение при пустых данных. Всё, что у графиков РАЗНОЕ — какие ряды, какие
 * пороги, какие пометки на оси — остаётся снаружи: либо в `series`, либо в
 * `children`, куда кладутся любые элементы Recharts (`ReferenceLine`,
 * `ReferenceArea`, `Brush`, свой `Customized`-слой).
 *
 * У достройки два слота, и разница между ними — слоистая: `children` рисуются
 * ПОВЕРХ серий (порог, пометка, подпись), `background` — ПОД ними (подложки
 * дней, зоны нормы). Контекст, положенный поверх данных, перестаёт быть
 * контекстом и становится вуалью на них.
 *
 * Поэтому частный график страницы не форкает этот компонент, а достраивает
 * его. Форк — самый дорогой способ разойтись с системой: он выглядит как
 * копия ровно до первой правки токена.
 *
 * ЧЕГО ЗДЕСЬ НЕТ СОЗНАТЕЛЬНО:
 *
 *   - Второй оси Y. Две шкалы в одном поле дают пересечения, которых в данных
 *     нет: сдвинь любую из них — и «корреляция» пропадёт. Два измерения —
 *     это два графика или общая база индекса.
 *   - Анимации входа. Она стоит кадров на каждом ре-рендере и ничего не
 *     сообщает; включать её осмысленно точечно, а не по умолчанию.
 *
 * ПРОИЗВОДИТЕЛЬНОСТЬ. `data`, `series` и `margin` обязаны быть стабильными
 * ссылками (`useMemo`). Recharts ремонтирует детей при смене пропов, и
 * свежий массив на каждый рендер роняет и локальный state внутри слоёв, и
 * кадры — в DigitalCast это уже стоило нам отдельного разбора.
 *
 * @format
 */

import {useEffect, useId, useRef, useState} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {ChartLegend} from './ChartLegend';
import {ChartTooltip} from './ChartTooltip';
import {
  ACTIVE_DOT_MAX,
  ACTIVE_DOT_MIN,
  ACTIVE_DOT_RADIUS,
  AREA_GRADIENT_STOPS,
  AXIS_COLOR,
  CURSOR_BAND_OPACITY,
  CURSOR_COLOR,
  CURSOR_LINE_OPACITY,
  DASH_FORECAST,
  DEFAULT_HEIGHT,
  GLOW_BLUR,
  GRID_COLOR,
  GRID_DASH,
  PLOT_RIGHT_MARGIN,
  STROKE_WIDTH,
  TICK_COLOR,
  Y_AXIS_WIDTH,
  activeVar,
  colorVar,
  seriesVar,
  shadeBottom,
} from './tokens';
import type {ChartProps, ChartSeries} from './types';

const Frame = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$height: number; $glows: {className: string; color: string}[]}>(({$height, $glows}) => ({
  width: '100%',
  height: $height,

  // Свечение задаётся CSS-ом, а не атрибутом `filter` на серии: Recharts
  // прогоняет пропы через свой фильтр допустимых SVG-атрибутов и `filter`
  // до элемента не доносит — молча, без ошибки.
  ...Object.fromEntries(
    $glows.map(({className, color}) => [
      `& .${className} .recharts-curve, & .${className} .recharts-dot`,
      {filter: `drop-shadow(0 0 ${GLOW_BLUR}px ${color})`},
    ]),
  ),
  // Канва фокусируема с клавиатуры (accessibilityLayer), но рамку по клику
  // мышью не показываем: у графика нет действия, которое клик бы запускал, и
  // рамка сообщала бы о готовности к чему-то несуществующему. Клавиатурная
  // навигация по точкам при этом остаётся.
  '& .recharts-surface:focus, & .recharts-surface:focus-visible': {
    outline: 'none',
  },
}));

const Empty = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$height: number}>(({$height}) => ({
  ...text('body.s'),
  color: surfaceVar('onSurfaceMuted'),
  height: $height,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
}));

const tickStyle = {
  ...text('body.s'),
  fill: TICK_COLOR,
} as const;

// Подписи осей рисуются в SVG, где `line-height` не работает вовсе, а
// `letter-spacing` в единицах em считается от другого кегля. Берём из роли
// только то, что SVG действительно применит.
const svgTick = {
  fontSize: tickStyle.fontSize,
  fontWeight: tickStyle.fontWeight,
  fill: TICK_COLOR,
};

const defaultNumberFormat = (value: number) => value.toLocaleString('ru-RU');

function seriesColor(s: ChartSeries, index: number): string {
  return s.color ? colorVar(s.color) : seriesVar(index);
}

/** Цвет кромки: свой, если задан, иначе тот же, что у заливки. */
function outlineColor(s: ChartSeries, index: number): string {
  return s.outlineColor ? colorVar(s.outlineColor) : seriesColor(s, index);
}

/**
 * Курсор-полоса: подсвечивает всю колонку дня, а не границу между днями.
 *
 * Своя реализация нужна потому, что `cursor={{fill}}` в Recharts 2.x
 * оборачивается в `Curve` — линию, у которой заливки нет вовсе, и проп
 * молча ничего не делает. Ширина полосы берётся из шага категории:
 * область построения, делённая на число точек.
 */
interface BandCursorProps {
  points?: {x: number; y: number}[];
  width?: number;
  count: number;
}

function BandCursor({points, width, count}: BandCursorProps) {
  const first = points?.[0];
  const second = points?.[1];
  if (!first || !second) return null;

  const band = Math.max(2, (width ?? 0) / Math.max(count, 1));
  const top = Math.min(first.y, second.y);
  const height = Math.abs(second.y - first.y);

  return (
    <rect
      x={first.x - band / 2}
      y={top}
      width={band}
      height={height}
      fill={CURSOR_COLOR}
      fillOpacity={CURSOR_BAND_OPACITY}
      pointerEvents='none'
    />
  );
}

/** Что Recharts передаёт в `content` у `LabelList`. */
interface PointLabelProps {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  value?: string | number;
  index?: number;
}

const num = (v: string | number | undefined): number => (typeof v === 'number' ? v : Number(v));

/**
 * Подпись точки: текст над маркером, по центру столбца.
 *
 * Рисуется в SVG, поэтому кегль и цвет берутся явно — роль целиком сюда не
 * переносится (`line-height` в SVG не работает). Тень под текстом не нужна:
 * подпись стоит НАД маркером, на фоне карточки, а не поверх заливки.
 */
function renderPointLabel(slot: PointLabelProps, label: string) {
  const x = num(slot.x) + num(slot.width) / 2;
  const y = num(slot.y) - 6;
  return (
    <text
      x={x}
      y={y}
      textAnchor='middle'
      fontSize={svgTick.fontSize}
      fontWeight={500}
      fill='var(--ads-palette-surface-onSurface)'
    >
      {label}
    </text>
  );
}

export function Chart({
  data,
  series,
  xKey,
  xFormat,
  yFormat = defaultNumberFormat,
  height = DEFAULT_HEIGHT,
  grid = true,
  yAxis = false,
  xAxis = true,
  legend = true,
  tooltip = true,
  yFromZero = false,
  syncId,
  cursor = 'line',
  emptyText = 'Нет данных за период',
  ariaLabel,
  children,
  background,
  className,
}: ChartProps) {
  // id градиентов глобальны в документе, а `fill="url(#id)"` резолвится в
  // ПЕРВЫЙ подходящий def. Два графика с одинаковым ключом серии на одной
  // странице без этого суффикса покрасились бы заливкой соседа.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');

  // Ширина области построения нужна активной точке: её диаметр равен колонке
  // дня, а колонку знает только отрисованный график. Меряем сам контейнер и
  // вычитаем поля — ResponsiveContainer считает так же.
  const frameRef = useRef<HTMLDivElement>(null);
  const [frameWidth, setFrameWidth] = useState(0);

  useEffect(() => {
    const node = frameRef.current;
    if (!node) return;
    setFrameWidth(node.clientWidth);
    const observer = new ResizeObserver(([entry]) => setFrameWidth(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (!data.length || !series.length) {
    return <Empty $height={height}>{emptyText}</Empty>;
  }

  // `<defs>` заводится только тем областям, что рисуются градиентом: у
  // сплошной заливки заливка и есть цвет серии.
  const areaFill = (s: ChartSeries) => s.fill ?? 'gradient';
  const isGradientArea = (s: ChartSeries) =>
    (s.type ?? 'line') === 'area' && areaFill(s) !== 'solid';
  const hasGradient = series.some(isGradientArea);

  // Подпись стоит НАД маркером, а самый высокий маркер упирается в потолок
  // области построения — без запаса подпись максимума обрезается краем SVG
  // (и молча: она просто исчезает, а остальные остаются на месте).
  const topMargin = series.some(s => s.pointLabel) ? 24 : 8;

  const glowClass = (index: number) => `ads-chart-glow-${uid}-${index}`;
  const glows = series
    .map((s, index) => ({s, index}))
    .filter(({s}) => s.glow)
    .map(({s, index}) => ({className: glowClass(index), color: outlineColor(s, index)}));

  // Диаметр активной точки — ровно колонка дня: точка тогда читается как
  // «этот день целиком», в тон подсветке-полосе под ней. Пол-пикселя ужимаем,
  // чтобы соседние дни не смыкались в сплошную ленту.
  const plotWidth = Math.max(0, frameWidth - (yAxis ? Y_AXIS_WIDTH : 0) - 8);
  const bandRadius = plotWidth ? plotWidth / data.length / 2 : ACTIVE_DOT_RADIUS;
  // Границы — от вырождения: на сотне точек колонка тоньше волоса, на десятке
  // раздувается в блин, который перекрывает соседей вместе с их значениями.
  const activeDotRadius = Math.min(Math.max(bandRadius, ACTIVE_DOT_MIN), ACTIVE_DOT_MAX);

  // При курсоре-полосе точка не нужна: день уже назван подсветкой, а кружок
  // поверх неё повторяет то же самое — и, будучи размером в ту же колонку,
  // читается не как маркер, а как грязь на линии.
  const activeDotFor = (s: ChartSeries, index: number) =>
    cursor === 'band'
      ? (false as const)
      : {
          r: activeDotRadius,
          fill: s.color ? activeVar(s.color) : seriesVar(index),
          stroke: 'none',
        };

  return (
    <Stack gap='s' className={className}>
      <Frame $height={height} $glows={glows} ref={frameRef}>
        <ResponsiveContainer width='100%' height='100%'>
          <ComposedChart
            data={data}
            syncId={syncId}
            accessibilityLayer
            aria-label={ariaLabel}
            margin={{top: topMargin, right: PLOT_RIGHT_MARGIN, bottom: 0, left: 0}}
          >
            {hasGradient && (
              <defs>
                {series.map((s, index) =>
                  isGradientArea(s) ? (
                    <linearGradient
                      key={s.id}
                      id={`ads-chart-${uid}-${index}`}
                      x1='0'
                      y1='0'
                      x2='0'
                      y2='1'
                    >
                      {areaFill(s) === 'shaded' ? (
                        <>
                          <stop offset='0%' stopColor={seriesColor(s, index)} />
                          <stop offset='100%' stopColor={shadeBottom(seriesColor(s, index))} />
                        </>
                      ) : (
                        AREA_GRADIENT_STOPS.map(stop => (
                          <stop
                            key={stop.offset}
                            offset={stop.offset}
                            stopColor={seriesColor(s, index)}
                            stopOpacity={stop.opacity}
                          />
                        ))
                      )}
                    </linearGradient>
                  ) : null,
                )}
              </defs>
            )}


            {/* Фон — первым слоем, до сетки и серий. Сетка ложится поверх
                подложки сознательно: она нужна для счёта значений и на
                залитом дне тоже. */}
            {background}

            {grid && (
              <CartesianGrid
                stroke={GRID_COLOR}
                strokeDasharray={GRID_DASH}
                vertical={false}
              />
            )}

            {/* Оси рендерятся всегда и лишь прячутся: `<XAxis>` — это ещё и
                место, где живёт привязка к `xKey`. Без неё Recharts
                подписывает точку её индексом, и тултип показывает «64»
                вместо «4.05» — молча, потому что число выглядит правдоподобно. */}
            <XAxis
              dataKey={xKey}
              hide={!xAxis}
              tickFormatter={xFormat}
              tick={svgTick}
              tickLine={false}
              axisLine={{stroke: AXIS_COLOR}}
              interval='preserveStartEnd'
              minTickGap={24}
            />

            <YAxis
              hide={!yAxis}
              tickFormatter={yFormat}
              tick={svgTick}
              tickLine={false}
              axisLine={false}
              width={Y_AXIS_WIDTH}
              domain={yFromZero ? [0, 'auto'] : undefined}
            />

            {series.map((s, index) => {
              // Серия «только для тултипа» ничего не рисует: её значение уже
              // изображено — например, как верхняя граница стека.
              if (s.tooltipOnly) return null;

              const color = seriesColor(s, index);
              const type = s.type ?? 'line';
              const common = {
                dataKey: s.id,
                name: s.label,
                stackId: s.stackId,
                isAnimationActive: false,
              };

              const pointLabels = s.pointLabel ? (
                <LabelList
                  dataKey={s.id}
                  content={(slot: PointLabelProps) => {
                    const label = s.pointLabel?.(num(slot.value), slot.index ?? -1);
                    return label ? renderPointLabel(slot, label) : null;
                  }}
                />
              ) : null;

              if (type === 'bar') {
                return (
                  <Bar key={s.id} {...common} fill={color} radius={[4, 4, 0, 0]} maxBarSize={40}>
                    {pointLabels}
                  </Bar>
                );
              }

              if (type === 'area') {
                const solid = areaFill(s) === 'solid';
                // Поднятая кромка рисуется отдельной линией после всех слоёв,
                // поэтому у самой области ни обводки, ни активной точки нет —
                // иначе они были бы нарисованы дважды, один раз под соседом.
                const outlined = (s.outline ?? true) && !s.raiseOutline;
                return (
                  <Area
                    key={s.id}
                    {...common}
                    className={s.glow && !s.raiseOutline ? glowClass(index) : undefined}
                    type='monotone'
                    stroke={outlined ? outlineColor(s, index) : 'none'}
                    strokeWidth={outlined ? STROKE_WIDTH : 0}
                    strokeDasharray={s.dashed ? DASH_FORECAST : undefined}
                    fill={solid ? color : `url(#ads-chart-${uid}-${index})`}
                    fillOpacity={1}
                    dot={false}
                    activeDot={s.raiseOutline ? false : activeDotFor(s, index)}
                  >
                    {pointLabels}
                  </Area>
                );
              }

              return (
                <Line
                  key={s.id}
                  {...common}
                  className={s.glow ? glowClass(index) : undefined}
                  type='monotone'
                  stroke={color}
                  strokeWidth={STROKE_WIDTH}
                  strokeDasharray={s.dashed ? DASH_FORECAST : undefined}
                  dot={false}
                  activeDot={activeDotFor(s, index)}
                >
                  {pointLabels}
                </Line>
              );
            })}

            {/* Поднятые кромки — последними, поверх всех слоёв. Это та же
                величина, что уже нарисована областью, поэтому из легенды и
                тултипа линия исключена: второй строкой она сообщила бы
                читателю о второй серии, которой нет. */}
            {series.map((s, index) =>
              s.raiseOutline && !s.tooltipOnly && (s.type ?? 'line') === 'area' ? (
                <Line
                  key={`${s.id}-outline`}
                  dataKey={s.id}
                  className={s.glow ? glowClass(index) : undefined}
                  type='monotone'
                  stroke={outlineColor(s, index)}
                  strokeWidth={STROKE_WIDTH}
                  strokeDasharray={s.dashed ? DASH_FORECAST : undefined}
                  dot={false}
                  activeDot={activeDotFor(s, index)}
                  isAnimationActive={false}
                  legendType='none'
                  tooltipType='none'
                />
              ) : null,
            )}

            {children}

            {/* Тултип стоит последним ради курсора: Recharts рисует его в том
                же порядке, в каком элементы перечислены, и подсветка дня,
                объявленная раньше слоёв, оказалась бы под ними — то есть
                невидимой под плотной заливкой. */}
            {tooltip && (
              <Tooltip
                content={<ChartTooltip series={series} xFormat={xFormat} />}
                cursor={
                  cursor === 'band' ? (
                    <BandCursor count={data.length} />
                  ) : (
                    {stroke: CURSOR_COLOR, strokeOpacity: CURSOR_LINE_OPACITY, strokeWidth: 1}
                  )
                }
                animationDuration={0}
                wrapperStyle={{outline: 'none'}}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Frame>

      {legend && <ChartLegend series={series} />}
    </Stack>
  );
}
